import Button from "components/Button/Button";
import { useState } from "react";
import styles from "./CurrentRoute.module.scss";
import { ReactComponent as DraggableIcon } from "assets/draggable.svg";
import { ReactComponent as ExpandIcon } from "assets/expand.svg";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { store } from 'stores/Store';
import { Tab } from "Modules/MapRouter";
import React from "react";
import useExternalApiService from "helpers/ExternalApiService";
import SearchModal from "./components/SearchModal";
import { ReactComponent as HouseIcon } from "assets/house.svg";
import { ReactComponent as CloseIcon } from "assets/xCircle.svg";
import { ReactComponent as FlagIcon } from "assets/flag.svg";
import { date2usDotDate } from "helpers";
import MeetingModal from "./components/MeetingModal";
import StartTimeModal from "./components/StartTimeModal";
import Toastr from "components/Toastr";
import { decode } from "@googlemaps/polyline-codec";
import RoutesTab from "Modules/PractitionerBaseballCard/components/RoutesTab";

interface IMapRouterProps {
  setSelectedTab: (tab: Tab) => void;
  setSelectedView: (view: string) => void;
  currentRouteName: string;
  currentRoute: any;
  clearCurrentRoute: () => void;
  setHasRouteChanged: (hasRouteChanged: boolean) => void;
  setShowUnsavedChangesModal: (showUnsavedChangesModal: boolean) => void;
  hasRouteChanged: boolean;
  showUnsavedChangesModal: boolean;
  clickedClear: boolean;
  setClickedClear: (clickedClear: boolean) => void;
  onRouteChange: (v: number) => void;
  onRouteSave: (id?: number) => void;
}

interface Waypoint {
  travelTime?: number;
  id?: number;
  address?: string;
  latitude?: string;
  longitude?: string;
  isFirstLocation?: boolean;
  isLastLocation?: boolean;
  start_time?: string;
  duration?: string;
  end_time?: string;
  arriveTime?: string;
  name?: string;
}

interface PlaceSuggestion {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
  types: string[];
}

interface PlacesResponse {
  success: boolean;
  error_msg: string;
  places: PlaceSuggestion[];
}
const parseStartTime = (startTime: string) => {
  if (!startTime) return "";
  if (startTime.includes("AM") || startTime.includes("PM")) {
    const [time, period] = startTime.split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    const isPM = period === 'PM';
    const adjustedHours = isPM && hours !== 12 ? hours + 12 : (hours === 12 && period === 'AM' ? 0 : hours);
    
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
      
    return `${year}-${month}-${day} ${String(adjustedHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
  }
  const date = new Date(startTime);
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
}
const formatTime = (date: Date) => {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};
const CurrentRoute = ({ setSelectedTab, setSelectedView, currentRouteName, currentRoute, onRouteSave, clearCurrentRoute,
                        setHasRouteChanged, setShowUnsavedChangesModal, hasRouteChanged, showUnsavedChangesModal,
                        clickedClear, setClickedClear, onRouteChange }: IMapRouterProps) => {
  const [waypoints, setWaypoints] = useState<(Waypoint | null)[]>([null, null]);
  const [selectedTime, setSelectedTime] = useState('10:00');
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isFirstLocation, setIsFirstLocation] = useState(true);
  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false);
  const [selectedWaypoint, setSelectedWaypoint] = useState<any>(waypoints[1]);
  const [isDragging, setIsDragging] = useState(false);
  const [clickTime, setClickTime] = useState(new Date());
  const [isStartTimeModalOpen, setIsStartTimeModalOpen] = useState(false);
  const [firstStartTime, setFirstStartTime] = useState(formatTime(new Date()));
  const [timesInitialized, setTimesInitialized] = useState(false);
  const apiUrl = process.env.REACT_APP_EAI_URL;
  const { postData } = useExternalApiService(apiUrl);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isGoRouteModalOpen, setIsGoRouteModalOpen] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  // Update current time every minute


  React.useEffect(() => {
    if (clickedClear && !showUnsavedChangesModal) {
      setShowUnsavedChangesModal(false);
      setClickedClear(false);
      store.setSelectedPractitioners([]); 
      setWaypoints([null, null]);
      setHasRouteChanged(false);
      clearCurrentRoute();
      store.setCurrentDirections([]);
    }
  }, [showUnsavedChangesModal, clickedClear]);
  React.useEffect(() => {
    console.log("hasRouteChanged", hasRouteChanged);
  }, [hasRouteChanged]);


  const formatDateTimeToAMPM = (dateTimeStr: string) => {
    if (!dateTimeStr) return "";
    
    // Extract time from the datetime string
    const time = dateTimeStr.split(' ')[1];
    const [hours, minutes] = time.split(':').map(Number);
    
    // Convert to 12-hour format
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    
    // Return formatted time
    return `${String(displayHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
  }

  const handleRoutes = (opt: string) => {
    switch (opt) {
      case 'google':
        OpenMapsNavigation()
        break;
      case 'waze':
        OpenWazeNavigation()
        break;
      case 'apple':
        OpenAppleMapsNavigation()
        break;
    }
    setIsGoRouteModalOpen(false)
  }



  const OpenMapsNavigation = () => {
    console.log('selectedWaypoint', selectedWaypoint);
    
    // Check if we have a selected waypoint
    if (!selectedWaypoint || !selectedWaypoint.latitude || !selectedWaypoint.longitude) {
      console.log('No selected waypoint or missing coordinates');
      return;
    }

    // Build Google Maps URL for the current waypoint only
    const url = `https://www.google.com/maps/dir/?api=1&destination=${selectedWaypoint.latitude},${selectedWaypoint.longitude}`;
    // Open in new tab
    window.open(url, '_blank');
  }
  const OpenWazeNavigation = () => {
    // Get first and last waypoints
    const waypoints = store.selectedPractitioners;
    if (!waypoints.length) return;

    const origin = waypoints[0];
    const destination = waypoints[1] || waypoints[0]; // If only one point, use it as both origin and destination
    
    // Waze URL format with origin and destination
    const url = `https://waze.com/ul?ll=${destination.latitude},${destination.longitude}&navigate=yes&from=${origin.latitude},${origin.longitude}`;
    
    window.open(url, '_blank');
  }

  const OpenAppleMapsNavigation = () => {
    const waypoints = store.selectedPractitioners;
    if (!waypoints.length) return;

    const validWaypoints = waypoints.filter(waypoint => 
      waypoint.latitude !== null && waypoint.longitude !== null
    );

    if (validWaypoints.length === 0) return;

    const waypointsString = validWaypoints.map((waypoint: any, index: number) => {
      // First point is source, rest are destinations
      const prefix = index === 0 ? 'saddr' : `daddr`;
      return `${prefix}=${waypoint.latitude},${waypoint.longitude}`;
    }).join('&');

    // Format: maps://?saddr={start_lat},{start_lng}&daddr={dest_lat},{dest_lng}
    const url = `maps://?${waypointsString}`;
    console.log('url', url);
    window.open(url, '_blank');
  }

  React.useEffect(() => {
    onRouteChange(waypoints?.filter((w) => w)?.length || 0);
  }, [waypoints])

  React.useEffect(() => {
    console.log("first useEffect");
    firstStartTimeUpdate(parseStartTime(store.selectedPractitioners[0]?.start_time));
    if(currentRoute?.practitioner_ids && store.selectedPractitioners.length === 0) {
      const getPractitioners = async () => {
        const practitioners = await Promise.all(
          currentRoute.practitioner_ids.map(async (id: number) => {
            const response = await postData('/api/get_practitioner_by_id', {
              practitioner_id: id
            });
            return response.practitioner;
          })
        );
        let firstLocation: any = null;
        let lastLocation: any = null;

        if (currentRoute.locations[0].is_starting_location == "1") {
          firstLocation = {
            address: currentRoute.locations[0].address,
            latitude: currentRoute.locations[0].latitude,
            longitude: currentRoute.locations[0].longitude,
            isFirstLocation: true,
            start_time: currentRoute.locations[0]?.start_time
          }
        } 
        if (currentRoute.locations[currentRoute.locations.length - 1].is_ending_location == "1") {
          lastLocation = {
            address: currentRoute.locations[currentRoute.locations.length - 1].address,
            latitude: currentRoute.locations[currentRoute.locations.length - 1].latitude,
            longitude: currentRoute.locations[currentRoute.locations.length - 1].longitude,
            isLastLocation: true
          }
        }
        // Update both waypoints and store
        const startedWaypoints = [firstLocation, ...practitioners, lastLocation].map((waypoint: any, index: number) => {
          console.log("waypointx",waypoint)
          if(waypoint) {
            waypoint.start_time = currentRoute.locations[index].start_time;
            waypoint.duration = parseInt(currentRoute.locations[index].duration);
            return waypoint;
          }
          return null; // Explicitly return null when waypoint is falsy
        });
        console.log("startedWaypoints",startedWaypoints)
        setWaypoints(startedWaypoints.filter(wp => wp !== null));

        store.setSelectedPractitioners(startedWaypoints.filter(wp => wp !== null));
      };
      getPractitioners();
    }else{
     setWaypoints(store.selectedPractitioners);
    }
  }, [currentRoute]);

  // Add useEffect to update waypoints when selectedPractitioners changes
  React.useEffect(() => {
    // Only run this effect if currentRoute is already loaded and we have selectedPractitioners
    if (store.selectedPractitioners.length > 0 && !currentRoute?.is_loading) {
 
      let firstLocation: any = null;
      let lastLocation: any = null;
      if (store.selectedPractitioners[0]?.isFirstLocation) {
        firstLocation = {
          address: store.selectedPractitioners[0].address,
          latitude: store.selectedPractitioners[0].latitude,
          longitude: store.selectedPractitioners[0].longitude,
          isFirstLocation: true,
          travelTime: store.selectedPractitioners[0].travelTime,
          start_time: store.selectedPractitioners[0]?.start_time
        }
      } 
      if (store.selectedPractitioners[store.selectedPractitioners.length - 1]?.isLastLocation) {
        lastLocation = {
          address: store.selectedPractitioners[store.selectedPractitioners.length - 1].address,
          latitude: store.selectedPractitioners[store.selectedPractitioners.length - 1].latitude,
          longitude: store.selectedPractitioners[store.selectedPractitioners.length - 1].longitude,
          isLastLocation: true,
          arriveTime: store.selectedPractitioners[store.selectedPractitioners.length - 1].arriveTime
        }
      }
      // Update waypoints with current first/last locations and all practitioners
      console.log("firstLocation",firstLocation)
      setWaypoints([firstLocation, ...store.selectedPractitioners.filter(p => !p?.isFirstLocation && !p?.isLastLocation), lastLocation]);
    }
  }, [store.selectedPractitioners, currentRoute]);

  // Add useEffect to initialize times when practitioners change
  React.useEffect(() => {
    
    const wp = waypoints.filter(wp => wp !== null);
    if (wp.length >= 2 && !timesInitialized) {
      console.log("optimizing route");
      setIsOptimizing(true);
      onOptimizeRoute(false);
      setTimesInitialized(true);    
      setTimeout(() => {
      setTimesInitialized(false);
      }, 1000);
    } else if (wp.length < 2) {
      console.log("not optimizing route");
    }else if (wp.length > 25) {
      console.log("not optimizing route");
      setToastMessage("You can only have 25 practitioners on a route");
      setShowToast(true);
    }

  }, [waypoints]);

  const handleSearchClick = (isFirst:boolean) => {
    setIsFirstLocation(isFirst);
    setIsSearchModalOpen(true);
  }

  const handleDragStart = () => {
    setIsDragging(true);
    setClickTime(new Date());
  };


  const renderSearchModal = () =>{
    return(
      <SearchModal
      isOpen={isSearchModalOpen}
      onClose={handleModalClose}
      searchTerm={searchTerm}
      suggestions={suggestions}
      onSuggestionClick={handleFirstLastLocation}
      firstLocation={isFirstLocation}
      onSearchChange={handleSearchChange}
    />
    )
  }
  const checkDuration = () => {
    if(!currentRoute){
      const currentDuration = waypoints.find((waypoint: any) => waypoint?.id === selectedWaypoint?.id)?.duration;
      if (currentDuration) {
        if (parseInt(currentDuration) < 60) {
          return `${currentDuration} mins`;
        } else {
          const hours = parseInt(currentDuration) / 60;
          return `${hours} hours`;
        }
      }
      return '15 mins';
    }
    const currentDuration = store.selectedPractitioners?.find((practitioner: any) => practitioner.id === selectedWaypoint.id)?.duration;
    if (!currentDuration) return '15 mins';
    if (currentDuration < 60) {
      return `${currentDuration} mins`;
    } else {
      const hours = currentDuration / 60;
      return `${hours} hours`;
    }
  }

  const checkStartTime = () => {
    if(!currentRoute){
      const currentStartTime = selectedWaypoint?.start_time;
      if (currentStartTime) {
        return formatDateTimeToAMPM(currentStartTime);
      }
      return '';
    }
    const currentStartTime = store.selectedPractitioners?.find((practitioner: any) => practitioner.id === selectedWaypoint.id)?.start_time;

    return currentStartTime?formatDateTimeToAMPM(currentStartTime):'';
  }

  const parseLength = (length: number) => {
    if (!length) return '15m';
    if (length < 60) {
      return `${length} m`;
    } else {
      const hours = length / 60;
      return `${hours} h`;
    }
  }
  const renderMeetingModal = () => {
    if (!selectedWaypoint) return null;
    return (
      <MeetingModal
        isOpen={isMeetingModalOpen}
        practitioner={selectedWaypoint}
        onClose={() => {
          setIsMeetingModalOpen(false);
          setSelectedWaypoint(null);
        }}
        updatePractitioner={updatePractitioner}
        onDelete={() => {
          handlePractitionerRemoval(selectedWaypoint);
          setIsMeetingModalOpen(false);
          setSelectedWaypoint(null);
        }}
        setSelectedView={setSelectedView}
        checkDuration={checkDuration}
        checkStartTime={checkStartTime}
        setGoRouteModalIsOpen={setIsGoRouteModalOpen}
      />
    );
  }

  const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    const response = await getPlacesAutocomplete(value) as PlacesResponse;
    if (response.success) {
      setSuggestions(response.places);
    }
  }

    // get latitude and longitude from location
    const getPlaceDetails = async (placeId: string) => {
      try {
        const result = await postData('/api/google_places_details', {
          place_id: placeId
        });
        if (result.success) {
          return {
            latitude: result.place.geometry.location.lat,
            longitude: result.place.geometry.location.lng
          };
        }
        throw new Error(result.error_msg || 'Failed to get place details');
      } catch (error) {
        console.error('Error getting place details:', error);
        throw error;
      }
    };

  const handleFirstLastLocation = async (place: any, isFirstLocation: boolean, currentLocation: boolean) => {
    let location: any;
    try {
      if(currentLocation) {
        location = {
          address: "Current Location",
          latitude: place.geometry.location.lat,
          longitude: place.geometry.location.lng
        };
      } else {
        const { latitude, longitude } = await getPlaceDetails(place.place_id);
        location = {
          address: place.description,
          latitude,
          longitude
        };
      }
      if(isFirstLocation) {
        setWaypoints(prev => {
          let newWaypoints;
          if (prev[0] == null) {
            newWaypoints = [{...location, isFirstLocation: true}, ...prev.slice(1)];
          } else {
            newWaypoints = [{...location, isFirstLocation: true}, ...prev];
          }
          store.setSelectedPractitioners(newWaypoints.filter(wp => wp !== null));
          setHasRouteChanged(true);
          setFirstStartTime(formatTime(new Date()));
          return newWaypoints;
        });
      } else {
        setWaypoints(prev => {
          let newWaypoints;
          if (prev[prev.length - 1] == null) {
            newWaypoints = [...prev.slice(0, -1), {...location, isLastLocation: true}];
          } else {
            newWaypoints = [...prev, {...location, isLastLocation: true}];
          }
          store.setSelectedPractitioners(newWaypoints.filter(wp => wp !== null));
          setHasRouteChanged(true);
          return newWaypoints;
        });
      }
      setSearchTerm('');
      setSuggestions([]);
      setIsSearchModalOpen(false);
    } catch (error) {
      console.error('Error setting location:', error);
    }
  }

  const handlePractitionerRemoval = (practitioner: any) => {
    store.removeFromRoute(practitioner);
    setWaypoints(prev => {
      const newWaypoints = prev.filter(wp => wp?.id !== practitioner?.id);
      store.setSelectedPractitioners(newWaypoints.filter(wp => wp !== null));
      setHasRouteChanged(true);
      return newWaypoints;
    });
  };

  const handleModalClose = () => {
    setIsSearchModalOpen(false);
    setSuggestions([]);
  }
  const updatePractitioner = async (practitionerId: number, updateData: any) => {
    // Update the waypoint in the local state while preserving order
    const updatedWaypoints = waypoints.map(wp => {
      if (wp === null) return wp;
      const {arriveTime, ...wpWithoutArriveTime} = wp;
      return wp.id === practitionerId 
        ? { ...wpWithoutArriveTime, duration: updateData.meeting_duration, start_time: updateData.start_time ? parseStartTime(updateData.start_time) : ""}
        : wpWithoutArriveTime;
    });
    store.setSelectedPractitioners(updatedWaypoints.filter(wp => wp !== null));
    setWaypoints(updatedWaypoints);
    
    // Update store with the same order as waypoints
  };

  const displayTime = (waypoint: any) => {
    if(waypoint.arriveTime && waypoint.arriveTime !== "") {
      return waypoint.arriveTime;
    }
    if(waypoint.start_time && waypoint.start_time !== "") {
      return parseStartTime(waypoint.start_time);
    }
    return formatTime(new Date());
  }
  const SortableItem = ({ id, waypoint, arriveTime, index, onClick }: { id: number, waypoint: any, arriveTime: string, index: number, onClick: () => void }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };
    const notNullWaypoint = waypoints.filter(wp => wp !== null);
    return (
      <div
        ref={setNodeRef} 
        style={style}
        onClick={() => {
          setSelectedWaypoint(waypoint);
          setIsMeetingModalOpen(true);
        }}
      >
        <div key={waypoint?.id}>
          <div className={styles.waypointSubWrapper}>
            <div
              key={waypoint?.id} className={styles.waypointWapper}>
              <DraggableIcon 
                {...attributes}
                {...listeners}
                style={{
                  cursor: "grab",
                  touchAction: "none",
                  WebkitUserSelect: "none",
                  userSelect: "none",
                  WebkitTouchCallout: "none",
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation(); 
                }}
              />
              <div className={styles.waypointInfo}>
                <p className={styles.headerText}>{index + (waypoints[0]? 2 : 1)}. {waypoint?.name || waypoint?.primary_name || ""}</p>
                <div className={styles.travelTextContainer}>
                  <p className={styles.timeText}>{displayTime(waypoint)}</p>
                  <div className={styles.travelLengthContainer}>  
                    Length:
                    <p className={styles.travelLengthText}>{parseLength(waypoint?.duration)}</p>
                  </div>
                </div>
              </div>
              <div className={styles.expandIconContainer}>
                <ExpandIcon width={16} height={16} />
              </div>
            </div>
            {!isNaN(waypoint?.travelTime) && waypoint.id !== notNullWaypoint[notNullWaypoint.length - 1]?.id  && (
              <div className={styles.custonSplitter}>
                <div className={styles.travelTime}>
                  <p className={styles.travelTimeText}>Travel Time: {waypoint.travelTime} minutes</p>
                </div>
              </div>
            )}
          </div>

          </div>
      </div>
    );
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor),
    useSensor(TouchSensor)
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if(clickTime && new Date().getTime() - clickTime.getTime() < 100) {
      setSelectedWaypoint(waypoints[active?.id]);
      setIsMeetingModalOpen(true);
    }
    if (active?.id !== over?.id) {
      
      const oldIndex = active?.id;
      const newIndex = over?.id;
      setHasRouteChanged(true);
      const reorderedWaypoints = arrayMove(waypoints, oldIndex, newIndex);
      store.setSelectedPractitioners(reorderedWaypoints.filter(wp => wp !== null));
      console.log("reorderedWaypoints", reorderedWaypoints);
    }
    setTimeout(() => setIsDragging(false), 0);
  };

  const onClearLocations = (options?: { bypassModal?: boolean }) => {
    const shouldBypassModal = options?.bypassModal ?? false;

    if (hasRouteChanged && !shouldBypassModal) {
      setShowUnsavedChangesModal(true);
      setClickedClear(true);
    } else {
      store.setSelectedPractitioners([]);
      setWaypoints([null, null]);
      setHasRouteChanged(false);
      clearCurrentRoute();
      setClickedClear(false);
      store.setCurrentDirections([])
    }
  };

  const onSaveRoute = async () => {
    let newRouteId;
    if(currentRoute?.id) {
      await updateRoute();
    } else {
      newRouteId = await saveRoute();
    }

    if (currentRoute || newRouteId) {
      onRouteSave(currentRoute?.id || newRouteId);
      setHasRouteChanged(false); 
      onClearLocations({ bypassModal: true }); // Call with option to bypass modal
      setSelectedTab(Tab.SAVED_ROUTES);
    } 
  }

  const handleFirstLocationStartTime = () => {
    setSelectedWaypoint(waypoints[0]);
    setIsStartTimeModalOpen(true);
  }

  const saveRoute = async () => {
    const address = waypoints.map((waypoint: any) => waypoint?.address || "");
    const latitude = waypoints.map((waypoint: any) => waypoint?.latitude || "");
    const longitude = waypoints.map((waypoint: any) => waypoint?.longitude || "");
    const start_time = waypoints.map((waypoint: any) => waypoint?.start_time || "");
    const end_time = waypoints.map((waypoint: any) => waypoint?.end_time || "");
    const duration = waypoints.map((waypoint: any) => waypoint?.duration);
    const routeData = {
      "name": currentRouteName || "",
      "fmc_email": store.impersonatedUser,
      "first_location": waypoints[0]?.isFirstLocation || false,
      "last_location": waypoints[waypoints.length - 1]?.isLastLocation || false,
      "address": address,
      "latitude": latitude,
      "longitude": longitude,
      "start_time": start_time,
      "end_time": end_time,
      "duration": duration,
    }

    if (waypoints && waypoints.length > 0) {
      const { route_id } =await postData('/api/add_route', {
        route_data: routeData,
        practitioner_ids: waypoints.filter(wp => !wp?.isFirstLocation && !wp?.isLastLocation).map((waypoint: any) => waypoint?.id)
      });
      return route_id;
    }
    return 0;
  }

  const updateRoute = async () => {
    const address = waypoints.map((waypoint: any) => waypoint?.address || "");
    const latitude = waypoints.map((waypoint: any) => waypoint?.latitude || "");
    const longitude = waypoints.map((waypoint: any) => waypoint?.longitude || "");
    const start_time = waypoints.map((waypoint: any) => waypoint?.start_time || "");
    const end_time = waypoints.map((waypoint: any) => waypoint?.end_time || "");
    const duration = waypoints.map((waypoint: any) => waypoint?.duration);
    const routeData = {
      "name": currentRouteName || "",
      "fmc_email": store.impersonatedUser,
      "first_location": waypoints[0]?.isFirstLocation || false,
      "last_location": waypoints[waypoints.length - 1]?.isLastLocation || false,
      "address": address,
      "latitude": latitude,
      "longitude": longitude,
      "start_time": start_time,
      "end_time": end_time,
      "duration": duration,
    }
    await postData('/api/update_route', {
      route_id: currentRoute?.id,
      route_data: routeData,
      practitioner_ids: waypoints.filter(wp => !wp?.isFirstLocation && !wp?.isLastLocation).map((waypoint: any) => waypoint?.id)
    });
  }
  const onOptimizeRoute = async (waypointOrder?: boolean) => {
    if(waypointOrder) {
      setTimesInitialized(true);
      await onOptimizeRouteTours(waypointOrder).then(() => {
        setTimeout(() => {
          setTimesInitialized(false);
        }, 3000);
      });
      return
    }
    setTimesInitialized(true);
    setTimeout(() => {
      setTimesInitialized(false);
    }, 1000);
    const startingLocation = (waypoints[0] !== null) ? {
        lat: waypoints[0]?.latitude,
        lng: waypoints[0]?.longitude 
    } : {
      lat: waypoints[1]?.latitude,
      lng: waypoints[1]?.longitude
    }

    const endingLocation = (waypoints[waypoints.length - 1] !== null) ? {
        lat: waypoints[waypoints.length - 1]?.latitude,
        lng: waypoints[waypoints.length - 1]?.longitude
    } : {
      lat: waypoints[1]?.latitude,
      lng: waypoints[1]?.longitude
  }

    // Convert waypoints to required format
    const waypointData = waypoints.slice(1, -1).map(waypoint => ({
      lat: waypoint?.latitude,
      lng: waypoint?.longitude
    }));

    try {
      // Determine the initial local time for calculations
      let initialLocalTimeForCalculations: Date;
      if (waypoints[0] !== null) { // Case 1: Dedicated first location exists
        if (firstStartTime) { 
          const fullDateTimeString = parseStartTime(firstStartTime); 
          initialLocalTimeForCalculations = new Date(fullDateTimeString);
        } else { 
          initialLocalTimeForCalculations = new Date();
        }
      } else if (waypoints[1] !== null) { // Case 2: No dedicated first location, route starts with waypoints[1]

        if (waypoints[1]?.start_time) {
          initialLocalTimeForCalculations = new Date(waypoints[1].start_time);
          console.log("initialLocalTimeForCalculations", initialLocalTimeForCalculations);
        } else { 
          initialLocalTimeForCalculations = new Date();
        }
      } else { 
        initialLocalTimeForCalculations = new Date();
      }
      const rightNow = new Date();
      if (initialLocalTimeForCalculations.getTime() <= rightNow.getTime()) {
        initialLocalTimeForCalculations.setDate(initialLocalTimeForCalculations.getDate() + 1); 
      }
      
      // The Google API requires departureTime in UTC ISO format
      const apiDepartureTimeUTCString = initialLocalTimeForCalculations.toISOString();

      const optimizedRoute = await postData('/api/google_routes_optimization', {
        waypoints: waypointData,
        departureTime: apiDepartureTimeUTCString, 
        origin: startingLocation,
        destination: endingLocation,
        optimizeWaypointOrder: waypointOrder ?? true
      });

      const _currentDirections: number[][] = [];
      optimizedRoute?.data?.routes?.forEach((r: any) => {
        r?.legs.forEach((l: any) => {
          l?.steps.forEach((s: any) => {
            const encodedPolyline = s?.polyline?.encodedPolyline;
            const decodedPolyline = decode(encodedPolyline);
            _currentDirections.push(
              ...(decodedPolyline.map(([lat, lon]) => {
                return [lon, lat]
              }))
            );
          })
        });
      });
      store.setCurrentDirections(_currentDirections);
      
      if (optimizedRoute?.data?.routes?.[0]?.optimizedIntermediateWaypointIndex) {
        // Get middle waypoints
        const middleWaypoints = waypoints.slice(1, -1);

        // Create new array with optimized middle waypoints order
        const optimizedMiddleWaypoints = optimizedRoute.data.routes[0].optimizedIntermediateWaypointIndex.map(
          (index: number) => middleWaypoints[index]
        );

        // Combine first, optimized middle, and last waypoints
        const optimizedWaypoints = [
          waypoints[0],
          ...optimizedMiddleWaypoints,
          waypoints[waypoints.length - 1]
        ];

        // Add durations from route legs to waypoints
        if (optimizedRoute?.data?.routes?.[0]?.legs) {
          const legs = optimizedRoute.data.routes[0].legs;
          let accumulatedLocalTime = new Date(initialLocalTimeForCalculations.getTime());

          // First leg - only consider travel time
          if (legs[0]?.duration) {
            const firstLegDurationMinutes = Math.round(parseInt(legs[0].duration.replace('s','')) / 60);
            if (optimizedWaypoints[0]) {
              optimizedWaypoints[0].travelTime = firstLegDurationMinutes; 
            }
            
            accumulatedLocalTime.setMinutes(accumulatedLocalTime.getMinutes() + firstLegDurationMinutes);
            
            // optimizedWaypoints[1] is the first actual stop after the starting point
            if (optimizedWaypoints[1]) {
              optimizedWaypoints[1].arriveTime = formatTime(new Date(accumulatedLocalTime.getTime())); // Pass a copy
            }
          }

          // Subsequent legs - consider both travel time and previous stop duration
          for (let i = 1; i < legs.length; i++) {
            const legTravelDurationMinutes = Math.round(parseInt(legs[i].duration.replace('s','')) / 60);
            // optimizedWaypoints[i] is the waypoint *before* this current leg (legs[i])
            if (optimizedWaypoints[i]) {
                 optimizedWaypoints[i].travelTime = legTravelDurationMinutes; // Travel time from this waypoint
            }

            const previousStopServiceDurationMinutes = parseInt(optimizedWaypoints[i]?.duration ?? "0"); // Duration *at* optimizedWaypoints[i]
            
            accumulatedLocalTime.setMinutes(accumulatedLocalTime.getMinutes() + previousStopServiceDurationMinutes + legTravelDurationMinutes);
            
            // optimizedWaypoints[i + 1] is the waypoint *after* this current leg
            if (optimizedWaypoints[i + 1]) { 
              optimizedWaypoints[i + 1].arriveTime = formatTime(new Date(accumulatedLocalTime.getTime())); // Pass a copy
            }
          }
        }

        console.log(optimizedWaypoints);
        
        
        setWaypoints(optimizedWaypoints);
        store.setSelectedPractitioners(optimizedWaypoints.filter(wp => wp !== null));
        setHasRouteChanged(true);
      }else{
        const route = optimizedRoute?.data?.routes?.[0];
        const currentWaypointsCopy = waypoints.map(wp => wp ? { ...wp } : null);

        currentWaypointsCopy.forEach(wp => {
          if (wp) {
            wp.arriveTime = wp.arriveTime || ""; 
            wp.travelTime = wp.travelTime || 0;  
          }
        });

        if (route?.legs && route.legs.length > 0) {
            const legs = route.legs;
            let accumulatedLocalTime = new Date(initialLocalTimeForCalculations.getTime());
 
            if (legs[0]?.duration) {
                const firstLegDurationMinutes = Math.round(parseInt(legs[0].duration.replace('s','')) / 60);
                if (currentWaypointsCopy[0]) { 
                    currentWaypointsCopy[0]!.travelTime = firstLegDurationMinutes;
                }
                
                accumulatedLocalTime.setMinutes(accumulatedLocalTime.getMinutes() + firstLegDurationMinutes);
                if (currentWaypointsCopy[1]) { 
                    currentWaypointsCopy[1]!.arriveTime = formatTime(new Date(accumulatedLocalTime.getTime()));
                }
            }

            for (let i = 1; i < legs.length; i++) {
                const legTravelDurationMinutes = Math.round(parseInt(legs[i].duration.replace('s','')) / 60);
                
                if (currentWaypointsCopy[i]) {
                    currentWaypointsCopy[i]!.travelTime = legTravelDurationMinutes; 
                }
                
                const serviceDurationAtCurrentStop = parseInt(currentWaypointsCopy[i]?.duration ?? "0"); 
                
                accumulatedLocalTime.setMinutes(accumulatedLocalTime.getMinutes() + serviceDurationAtCurrentStop + legTravelDurationMinutes);
                
                if (currentWaypointsCopy[i+1]) { 
                    currentWaypointsCopy[i+1]!.arriveTime = formatTime(new Date(accumulatedLocalTime.getTime()));
                }
            }
            setWaypoints(currentWaypointsCopy);
            store.setSelectedPractitioners(currentWaypointsCopy.filter(wp => wp !== null) as Waypoint[]);
        } else {
            setWaypoints(currentWaypointsCopy);
            store.setSelectedPractitioners(currentWaypointsCopy.filter(wp => wp !== null) as Waypoint[]);
        }
      }
    } catch (error) {
      console.error('Error optimizing route:', error);
    }
    setIsOptimizing(false);
  }


  // ——— add at top if you don't already have it —————————————
  interface LatLng {
    lat: number;
    lng: number;
    name?: string;
}

interface RouteResponse {
  routes: Array<{
    beforeDepartureIndex?: Array<number>;
    skippedIntermediateWaypointIndex?: Array<number>;
    visits?: Array<{ startTime: string, duration: string }>;
    transitions?: Array<{ travelDuration: string }>;
    optimizedIntermediateWaypointIndex?: number[];
    legs: Array<{ duration: string, startTime: string }>;
    summary: {
      hasTrafficInfeasibilities: boolean;
      totalDuration: string;
      travelDuration: string;
      visitDuration: string;
      totalDistanceMeters: number;
      performedShipmentCount: number;
    };
  }>;
}

async function onOptimizeRouteTours(waypointOrder?: boolean): Promise<void> {
  if(waypoints.length > 25) {
    setToastMessage("You cannot optimize a route with more than 25 practitioners");
    setShowToast(true);
    return;
  }
  // 1) UI spin
  setTimesInitialized(true);
  setTimeout(() => setTimesInitialized(false), 2000);

  // 2) Start & end LatLng
  let startLatLng = null;
  if(waypoints[0] !== null){
    startLatLng = { lat: +waypoints[0]!.latitude!, lng: +waypoints[0]!.longitude! };
  }
      
  let endLatLng = null;
  if(waypoints[waypoints.length - 1] !== null){
    endLatLng = { lat: +waypoints[waypoints.length - 1]!.latitude!, lng: +waypoints[waypoints.length - 1]!.longitude! };
  }
  // 3) Middle waypoints for the API
  const waypointData: LatLng[] = waypoints
    .filter(wp => wp !== null)
    .filter(wp=> !wp!.isFirstLocation && !wp!.isLastLocation)
    .map((wp) => ({
      name: wp!.name,
      lat: +wp!.latitude!,
      lng: +wp!.longitude!,
      startTime: wp!.start_time ? (new Date(wp!.start_time)).toISOString() : "",
      duration: wp!.duration
    }))
  console.log('waypointData', waypointData)
  // 4) Compute initialLocalTime (same logic as onOptimizeRoute)
  let initialLocalTime: Date;
  if (waypoints[0] !== null && firstStartTime) {
    initialLocalTime = new Date(parseStartTime(firstStartTime));
  } else if (waypoints[1]?.start_time) {
    initialLocalTime = new Date(waypoints[1]!.start_time!);
  } else {
    initialLocalTime = new Date();
  }
  if(waypoints.filter(wp=> wp?.isFirstLocation).length === 0){
    // Find the waypoint with the earliest start_time among non-first/last location waypoints
    const waypointsWithStartTime = waypoints
      .filter(wp => wp !== null && !wp.isFirstLocation && !wp.isLastLocation && wp.start_time)
      .sort((a, b) => a!.start_time!.localeCompare(b!.start_time!));
    if (waypointsWithStartTime.length > 0) {
      initialLocalTime = new Date(new Date(waypointsWithStartTime[0]!.start_time!).getTime() - 1);
    }else{
      initialLocalTime = new Date(initialLocalTime.getTime() - 60 * 1000);
    }
  }
 
  const departureTimeUtc = initialLocalTime.toISOString();
  try {
    // 5) Hit the Tours endpoint
    const res = await postData(
      '/api/google_routes_optimization_tours',
      {
        origin: startLatLng,
        destination: endLatLng,
        waypoints: waypointData,
        departureTime: departureTimeUtc,
        optimizeWaypointOrder: true,
        endTime: new Date(new Date(departureTimeUtc).getTime() + 12 * 60 * 60 * 1000).toISOString()
      }
    ) as {
      beforeDepartureIndex: any; data: RouteResponse 
};

    const route = res.data.routes[0];
    if(route.beforeDepartureIndex?.length! > 0) {
      const practitionerNames = route.beforeDepartureIndex?.map((index) => waypointData[index].name).join(', ')
      setToastMessage(`Time on ${practitionerNames} is set previous to departure time.`);
      setShowToast(true);
      return
    }
    if(route.skippedIntermediateWaypointIndex?.length! > 0 || route.summary.hasTrafficInfeasibilities) {
      console.log('skippedIntermediateWaypointIndex', route.skippedIntermediateWaypointIndex)
      setShowToast(true);
      setToastMessage(`Due to traffic conditions, the following locations with required start times are not possible: ${route.skippedIntermediateWaypointIndex?.map((index) => waypointData[index].name).join(', ')}.  Please remove the start time requirements in order to optimize your route.`);
      return
    }
    // ─── 6) Normalize into order + legs ─────────────────────────────
    let order: number[];
    let visits: Array<{startTime: string, duration: string }>;
    let legs: Array<{ duration: string, startTime: string }> | undefined;

    if (Array.isArray(route.optimizedIntermediateWaypointIndex) && Array.isArray(route.visits)) {
      // ComputeRoutes‐style (fallback)
      order = route.optimizedIntermediateWaypointIndex;
      visits  = route.visits.map((l) => ({ startTime: l.startTime, duration: l.duration }));
      legs = route.legs.map((t) => ({ duration: t.duration, startTime: t.startTime }));
    } else {
      throw new Error('Unexpected route shape');
    }
    // ────────────────────────────────────────────────────────────────

    // 7) Rebuild optimized waypoints array
    const firstWaypoint = waypoints.filter(wp => wp?.isFirstLocation);
    const lastWaypoint = waypoints.filter(wp=> wp?.isLastLocation);
    const middle = waypoints.filter(wp=> wp !== null).filter(wp=> !wp!.isFirstLocation && !wp!.isLastLocation);
    
    // Create deep copies to preserve original start_time values
    const optimizedMiddle = order.map((idx: number) => {
      const originalWaypoint = middle[idx]!;
      return {
        ...originalWaypoint,
        // Explicitly preserve the original start_time constraint
        start_time: originalWaypoint.start_time
      };
    });
    
    console.log('optimizedMiddle', optimizedMiddle)
    let optimizedWaypoints: Array<Waypoint | null> = [
      ...firstWaypoint.map(wp => ({ ...wp, start_time: wp?.start_time })), // Preserve first location start_time
      ...optimizedMiddle,
      ...lastWaypoint
    ];
    optimizedWaypoints = optimizedWaypoints.filter(wp => wp !== null);
    // 8) Compute travelTime & arriveTime
    let clock = new Date(initialLocalTime.getTime());


    // subsequent legs → travel + service
    let legsTrueSize = legs.length;
    let visitPadding = 0;
    let legsStartIndex = 0;
    let legsPadding = 0;
    if(firstWaypoint.length > 0){ // if dedicated starting point, set paddings to calculattion indexes
      visitPadding = 1;
    }else{ 
      legsPadding = 1;
    }

    for (let i = 0; i < visits.length; i++) {
      const travelMin = Math.round(parseInt(legs[i + legsPadding].duration, 10) / 60);
      optimizedWaypoints[i]!.travelTime = travelMin;
      optimizedWaypoints[i + visitPadding]!.arriveTime = formatTime(new Date(visits[i]!.startTime));
    }
    if(lastWaypoint.length > 0){ // if dedicated ending point, manually calculate for it
      let lastStartTime = new Date(legs[legs.length - 1]!.startTime);
      lastStartTime.setMinutes(lastStartTime.getMinutes() + optimizedWaypoints[optimizedWaypoints.length - 2]!.travelTime!);
      optimizedWaypoints[optimizedWaypoints.length - 1]!.arriveTime = formatTime(lastStartTime);
    }
    // 9) Commit to state
    setWaypoints(optimizedWaypoints);
    const finalWps = optimizedWaypoints.filter(
      (wp): wp is Waypoint => wp !== null
    );
    store.setSelectedPractitioners(finalWps);
    setHasRouteChanged(true);

  } catch (err) {
    console.error('Error optimizing via Tours:', err);
  }
  setIsOptimizing(false);
}

  const getPlacesAutocomplete = async (input: string) => {
    return new Promise((resolve, reject) => {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const currentLocationString = `${lat},${lng}`;
          
          try {
            const result = await postData('/api/google_places_autocomplete', {
              query: input,
              location: currentLocationString,
            });
            resolve(result);
          } catch (error) {
            reject(error);
          }
        }, (error) => {
          reject(error);
        });
      } else {
        reject(new Error('Geolocation not supported'));
      }
    });
  };

  const firstStartTimeUpdate = (startTime: string) => {
      const firstWaypoint = waypoints.filter(wp => wp !== null)[0];
      console.log("firstWaypoint",firstWaypoint)
      setFirstStartTime(startTime);
      if (firstWaypoint) {
        firstWaypoint.start_time = startTime ? parseStartTime(startTime) : (firstStartTime ? firstStartTime : "");
        setWaypoints([firstWaypoint, ...waypoints.slice(1)]);
        store.setSelectedPractitioners([firstWaypoint, ...store.selectedPractitioners.slice(1)]);
     
      }
  }
  return (
    <div className={styles.currentRouteContainer}>
      <div className={styles.headerContainer}>
        <div className={styles.btnsContainer}>
          {/*<Button type="outline" onClick={() => onOptimizeRoute(true)}>OPTIMIZE</Button>*/}
          <div 
            className={styles["loading-button"]}
            onClick={() => {
              if (!isOptimizing) {
                onOptimizeRouteTours(true);
                setIsOptimizing(true);
              }
            }}>
            {isOptimizing && (
              <>
                <span className={styles["loading-button__loader"]}></span>
                <span className={styles["loading-button__loader-cover"]}></span>
              </>
            )}
          <Button type="outline" >OPTIMIZE</Button>
          </div>
          <Button type="outline" onClick={() => setSelectedTab(Tab.LIST_CUSTOMERS)}>ADD</Button>
        </div>
        <div className={styles.btnsContainer}>
          <Button type="outline" color="#E64B3780" textColor="#E64B37" onClick={() => onClearLocations()}>NEW ROUTE</Button>
          <Button type="outline" color="#4F7D9C" textColor="white" onClick={onSaveRoute}>SAVE</Button>
        </div>
      </div>

      {renderSearchModal()}
      {renderMeetingModal()}
      <StartTimeModal 
        isOpen={isStartTimeModalOpen} 
        onClose={() => setIsStartTimeModalOpen(false)} 
        startTime={firstStartTime} 
        setStartTime={firstStartTimeUpdate} 
        setIsStartTimeModalOpen={setIsStartTimeModalOpen}
      />
      <div className={styles.searchContainer}>
        {!waypoints[0] ? (waypoints.length > 2 ? (
          
          <div className={styles.searchInput} onClick={()=>handleSearchClick(true)}>
            <HouseIcon width={'25px'} height={'25px'} style={{marginRight: '15px',}}/>
            <span className={styles.placeholder}>Starting location</span>
          </div>
        ): null) : (
          <div className={styles.firstLocationContainer}>
            <div className={styles.locationContainer}>
              <div className={styles.locationNumber}>1.</div>
              <span className={styles.locationAddress}>{waypoints[0].address}</span>              
            </div>
            <div style={{display:'flex'}}>
              <CloseIcon style={{marginRight: '20px'}} width={30} height={30} onClick={() => {
                setFirstStartTime("");
                setWaypoints(prev => [null, ...prev.slice(1)]);
                setHasRouteChanged(true);
              }}/>
              <div onClick={() => handleFirstLocationStartTime()} className={styles.timeText}>
                {firstStartTime ? (firstStartTime) : formatTime(new Date())}
              </div>
            </div>
            {waypoints[0]?.travelTime ? (
              <div className={`${styles.custonSplitter} ${styles.firstLocationSplitter}`}>
                <div className={styles.travelTime}>
                  <p className={styles.travelTimeText}>Travel Time: {waypoints[0]?.travelTime} minutes</p>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
      <div >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={waypoints.slice(1, -1).map((_, index) => index+1)} strategy={verticalListSortingStrategy}>
            {waypoints.slice(1, -1).map((waypoint, index) => (
              <SortableItem 
                key={index+1} 
                id={index+1} 
                waypoint={waypoint} 
                arriveTime={waypoint?.arriveTime || ""}
                index={index}
                onClick={() => {}}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
      {!waypoints[waypoints.length - 1] ? (waypoints.length > 2 ? (
          <div className={styles.searchInput} onClick={()=>handleSearchClick(false)}>
            <FlagIcon width={'20px'} height={'20px'} style={{paddingLeft: '4px', marginRight: '15px'}}/>
            <span className={styles.placeholder}>Ending location</span>
          </div>
        ) : null) : (
          <div className={styles.firstLocationContainer}>
            <div className={styles.locationContainer}>
              <div className={styles.locationNumber}>{store.selectedPractitioners.length}.</div>
              <span className={styles.locationAddress}>{waypoints[waypoints?.length - 1]?.address}</span>              
            </div>
            <div style={{display:'flex'}}>
              <CloseIcon style={{marginRight: '20px'}} width={30} height={30} onClick={() => {
                setWaypoints(prev => [...prev.slice(0, -1), null]);
                setHasRouteChanged(true);
              }}/>
              <div className={styles.timeText}>
                {waypoints[waypoints.length - 1]?.arriveTime}
              </div>
            </div>
          </div>
        )}
      
      <RoutesTab isOpen={isGoRouteModalOpen} setIsOpen={setIsGoRouteModalOpen} onSelect={handleRoutes} />
      <Toastr closeTime={5000} backgroundColor={'#E64B37'} message={toastMessage} isVisible={showToast} onClose={() => setShowToast(false)} />
    </div>
  );
};

export default CurrentRoute;